import React, { useEffect, useState } from "react";
import { api, fmtPrice, mediaUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { OrderTimeline } from "@/components/OrderTimeline";
import { DeliveryMap } from "@/components/DeliveryMap";
import { statusLabel, statusColor } from "@/lib/status";
import { Package, Loader2 } from "lucide-react";

export default function Orders() {
  const { t } = useLang();
  const [orders, setOrders] = useState(null);

  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const anyActive = (orders || []).some((o) => o.courier_id && o.status !== "delivered");
    if (!anyActive) return;
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, [orders]);

  if (orders === null) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-800" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-8">{t("my_orders")}</h1>
      {orders.length === 0 ? (
        <div className="py-20 text-center text-stone-400" data-testid="no-orders"><Package className="w-12 h-12 mx-auto mb-3 text-stone-300" />{t("no_orders")}</div>
      ) : (
        <div className="space-y-5">
          {orders.map((o) => (
            <div key={o.id} className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid={`order-${o.id}`}>
              <div className="flex items-center justify-between p-5 border-b border-stone-100 flex-wrap gap-2">
                <div>
                  <p className="font-display font-bold">{o.order_no}</p>
                  <p className="text-xs text-stone-400">{new Date(o.created_at).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusColor(o.status)}`}>{statusLabel(t, o.status)}</span>
                  <span className="font-display font-extrabold text-emerald-800">{fmtPrice(o.total)}</span>
                </div>
              </div>
              <div className="grid md:grid-cols-[1fr_240px] gap-6 p-5">
                <div className="space-y-3">
                  {o.items.map((it) => (
                    <div key={it.product_id} className="flex gap-3 items-center">
                      <img src={mediaUrl(it.image)} alt={it.name} className="w-12 h-12 rounded-lg object-cover bg-stone-100" />
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium line-clamp-1">{it.name}</p><p className="text-xs text-stone-400">{it.quantity} × {fmtPrice(it.price)}</p></div>
                    </div>
                  ))}
                  <div className="text-xs text-stone-500 pt-2 border-t border-stone-100">
                    <p>{o.address?.full_name} · {o.address?.phone}</p>
                    <p>{o.address?.street} {o.address?.district}, {o.address?.city}</p>
                    {o.courier_name && <p className="text-emerald-700 mt-1">🛵 {o.courier_name}</p>}
                  </div>
                </div>
                <div className="md:border-l md:border-stone-100 md:pl-6">
                  <OrderTimeline status={o.status} />
                  {o.courier_id && o.status !== "delivered" && o.courier_location && (
                    <div className="mt-4">
                      <p className="text-xs font-semibold text-emerald-800 mb-2 flex items-center gap-1">🛵 {t("live_tracking")}</p>
                      <DeliveryMap location={o.courier_location} path={o.courier_path} destination={o.dest_location} />
                    </div>
                  )}
                  {o.courier_id && o.status !== "delivered" && !o.courier_location && (
                    <p className="text-xs text-stone-400 mt-3">{t("awaiting_location")}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
